// Bodyweight goal section for the Summary view.
// Two states: goal set (projection chart + Goal/Projected cards + narrative)
// or empty (form that POSTs to create one). Mirrors SummaryHero.js's pattern —
// the data (goalData/goalSeries) is computed in SummaryView.js and passed down.

import React, { useMemo, useState, memo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import submit from "submit-form";
import { useTranslation } from "react-i18next";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

import { toDisplayWeight, toDisplayRate, toKg, evenlySpacedTicks, GOAL_STATE_COLORS, CHART_THEME, computeAxisDomain } from "./viewTokens";
import { ChartLegend, TooltipShell } from "./viewAtoms";
import SvgIcon from "../../../../../parts/SvgIcon";
import DateInput from "../../../../../parts/inputs/date-input";
import { useCenteredTooltip } from "../../../../../hooks/useCenteredTooltip";
import { GoalAdjustModal } from "./GoalAdjustModal";

// Theme-aware, shared with SummaryHero.js/Reports' own charts — see
// CHART_THEME (viewTokens.js) for the full "why". Matches the tick color/size
// and grid line style used by the "Is the plan working?" chart, per the
// user's explicit ask.
const { tick: CHART_TICK, tickFontSize: CHART_TICK_SIZE, grid: CHART_GRID } = CHART_THEME;

/* ---- i18n helper -------------------------------------------------------- */

const g = (t, key, opts) => t(`nutritionPage.progress.goal.${key}`, opts);

/* ---- narrative / state label helpers ------------------------------------ */

// Narrative text for the goal block — extracted to keep GoalSetDisplay simple.
// All weight-shaped numbers (ratePerWeek, targetWeight, reqRate) must already be
// in the caller's display unit; extraDeficit is kcal/day and is unit-agnostic.
const buildGoalNarrative = ({ ratePerWeek, isStale, movingToward, paceState, weeksToReach, weeksToTarget, targetWeight, utcTargetDate, reqRate, extraDeficit, weightUnit, t }) => {
  if (ratePerWeek == null) {
    return g(t, isStale ? "staleWeighIn" : "notEnoughData");
  }
  if (movingToward && paceState === "behind") {
    const weeksLate = Math.max(1, Math.round(weeksToReach - weeksToTarget));
    const reachDate = moment().add(weeksToReach, "weeks").format("MMM D");
    const weekWord = weeksLate === 1 ? g(t, "weekSingular") : g(t, "weekPlural");
    return g(t, "narrativeBehind", {
      rate: Math.abs(ratePerWeek), unit: weightUnit, target: targetWeight, date: reachDate,
      weeksLate, weekWord, reqRate: Math.abs(reqRate), extraDeficit,
    });
  }
  if (movingToward && paceState !== "offPace") {
    const targetDateStr = moment(String(utcTargetDate), "YYYYMMDD").format("MMM D");
    return g(t, "narrativeOnTrack", { rate: Math.abs(ratePerWeek), unit: weightUnit, target: targetWeight, date: targetDateStr });
  }
  const sign = ratePerWeek > 0 ? "+" : "";
  const targetDateStr = moment(String(utcTargetDate), "YYYYMMDD").format("MMM D");
  return g(t, "narrativeOffTrack", { target: targetWeight, unit: weightUnit, sign, rate: ratePerWeek, date: targetDateStr, reqRate: Math.abs(reqRate), extraDeficit });
};

// The verdict (paceState: onPace / ahead / behind / offPace) comes from
// buildGoalData, the same one the Bodyweight trend card and the Insights panel
// use. Not moving toward the goal at all is "off track" unless the projection
// still lands within tolerance.
// Exported so other bodyweight-goal-status consumers (e.g. Periodization's
// PhaseSummary) render the identical label/token, not a re-derived one.
export const goalStateLabel = ({ movingToward, paceState, t }) => {
  if (paceState === "offPace" || (!movingToward && paceState !== "onPace")) {
    return { label: g(t, "stateOffTrack"), token: "bad" };
  }
  if (paceState === "ahead") return { label: g(t, "stateAhead"), token: "good" };
  if (paceState === "behind") return { label: g(t, "stateBehindPace"), token: "warn" };
  return { label: g(t, "stateOnTrack"), token: "good" };
};
// Function to return the label for the goal state based on the kg short and need change.
// kgShort must already be in the caller's display unit; needChange stays in kg
// (only used for its sign, which convertUnit's linear scale always preserves).
const kgShortLabel = (kgShort, needChange, weightUnit, t) => {
  if (kgShort === 0) return g(t, "onTarget");
  const isAhead = kgShort * needChange > 0; // same sign = went past target = ahead
  if (isAhead) return g(t, "ahead", { value: Math.abs(kgShort), unit: weightUnit });
  return g(t, "short", { value: Math.abs(kgShort), unit: weightUnit });
};

/* ---- shared header (goal-set and empty states show the identical title) - */

const GoalHeader = ({ chip }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <div className="np-goal__header">
      <SvgIcon src="/img/new-icons/bullseye.svg" useCurrentColor classNames="np-goal__icon" />
      <span className="np-goal__title">{g(t, "title")}</span>
      {chip && (
        <span
          className="np-goal__chip"
          style={{
            color: GOAL_STATE_COLORS[chip.token]?.text,
            background: GOAL_STATE_COLORS[chip.token]?.bg,
          }}
        >
          {chip.label}
        </span>
      )}
    </div>
  );
};

GoalHeader.propTypes = {
  chip: PropTypes.shape({ label: PropTypes.string, token: PropTypes.string }),
};

/* ---- bodyweight goal chart ----------------------------------------------- */

const ACTUAL_COLOR = "#6ba6e8"; // blue — real weigh-ins
const GOAL_COLOR = "#8560F1"; // purple — goal reference line, fixed regardless of goal state
const EDIT_GRADIENT_ICON = "/img/new-icons/edit-gradient-icon.svg";

/* ---- custom chart tooltip ------------------------------------------------ */
// Same .np-hero__tooltip surface/row shape as ReportsIntakeChart.js's own
// IntakeTooltip — rows are omitted (not shown as "—") when that day has no
// actual/projected value, same convention as SummaryHero.js's ChartTooltip.

const GoalTooltip = ({ active, payload, weightUnit, projColor, onMeasureWidth }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <TooltipShell active={active} payload={payload} onMeasureWidth={onMeasureWidth} isEmpty={(pt) => !pt}>
      {(pt) => {
        const isToday = pt.utcDate === Number(moment().format("YYYYMMDD"));
        const dateLabel = moment(String(pt.utcDate), "YYYYMMDD").format("MMM D, YYYY");
        return (
          <>
            <div className="np-hero__tooltip-date">
              {dateLabel}{isToday ? ` (${g(t, "chart.today")})` : ""}
            </div>
            {pt.actual != null && (
              <div className="np-hero__tooltip-row" style={{ color: ACTUAL_COLOR }}>
                <span>{g(t, "chart.actual")}:</span>
                <span>{Math.round(pt.actual * 10) / 10} {weightUnit}</span>
              </div>
            )}
            {pt.projected != null && (
              <div className="np-hero__tooltip-row" style={{ color: projColor }}>
                <span>{g(t, "chart.projected")}:</span>
                <span>{Math.round(pt.projected * 10) / 10} {weightUnit}</span>
              </div>
            )}
          </>
        );
      }}
    </TooltipShell>
  );
};

GoalTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  weightUnit: PropTypes.string,
  projColor: PropTypes.string,
  onMeasureWidth: PropTypes.func,
};

/* ---- chart ----------------------------------------------------------------- */

// Projection chart: solid blue = actual weight,
// dashed projection (colored by goal state) running to the target date,
// dashed blue horizontal = the goal weight.
const GOAL_CHART_TICK_COUNT = 6;

const GoalChart = memo(({ series, targetWeight, projColor, weightUnit }) => {
  const { t } = useTranslation("athlete-portal");

  // Evenly spaced across the FULL domain, always including the last day —
  // see evenlySpacedTicks (viewTokens.js) for why interpolated, not stepped.
  const tickIndices = useMemo(
    () => evenlySpacedTicks(series.length - 1, GOAL_CHART_TICK_COUNT),
    [series]
  );

  // Y domain spans every weigh-in + the projection, trimmed the same
  // outlier-safe way as the kcal/weight axes in Hero/Reports (see
  // computeAxisDomain's own comment, viewTokens.js) — targetWeight is a
  // single fixed value (the goal line), not noisy data, so it's passed as
  // mustInclude rather than folded into the trimmed set (same reasoning as
  // TDEE elsewhere). Falls back to a plain min/max ±1 — NOT Recharts' raw
  // 0-anchored default — when computeAxisDomain bails: the most common
  // trigger here is a goal freshly set with only 2-3 weigh-ins logged so
  // far, which isn't a bad-data problem, just not enough points to trim.
  const weightDomain = useMemo(
    () => computeAxisDomain(
      series.flatMap((p) => [p.actual, p.projected]),
      { mustInclude: [targetWeight], minSpan: 10, roundTo: 1 }
    ),
    [series, targetWeight]
  );
  const [naiveMin, naiveMax] = useMemo(() => {
    const vals = [targetWeight];
    for (const p of series) {
      if (p.actual != null) vals.push(p.actual);
      if (p.projected != null) vals.push(p.projected);
    }
    const lo = Math.floor(Math.min(...vals) - 1);
    const hi = Math.ceil(Math.max(...vals) + 1);
    return [lo, hi];
  }, [series, targetWeight]);

  // Bottom legend — same coloredText swatch-row as HeroChart's, and the
  // Projected line gets the dashed-line swatch (it's drawn dashed on the
  // chart), same convention as Target in HeroChart/ReportsIntakeChart.js.
  const legendPayload = useMemo(
    () => [
      { value: g(t, "chart.weight"), color: ACTUAL_COLOR, dataKey: "actual" },
      { value: g(t, "chart.projected"), color: projColor, dataKey: "projected", swatchShape: "dashedLine" },
    ],
    [t, projColor]
  );

  // Manual tooltip centering — see useCenteredTooltip's own header comment
  // for the full "why" (Recharts doesn't do this by default). Same fix
  // already applied to ReportsIntakeChart.js/SummaryHero.js.
  const { containerRef, tooltipPosition, onMeasureWidth, chartHandlers } = useCenteredTooltip({
    initialWidth: 160,
  });

  if (!series.length) return null;

  return (
    <div ref={containerRef}>
      <ResponsiveContainer width="99%" height={220}>
        <ComposedChart data={series} margin={{ top: 32, right: 16, bottom: 0, left: 0 }} {...chartHandlers}>
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <XAxis
            dataKey="idx"
            type="number"
            domain={[0, Math.max(series.length - 1, 1)]}
            ticks={tickIndices}
            tickFormatter={(i) => series[i]?.name ?? ""}
            tick={{ fontSize: CHART_TICK_SIZE, fill: CHART_TICK }}
            tickLine={false}
            axisLine={false}
            padding={{ left: 32, right: 32 }}
          />
          <YAxis
            domain={weightDomain ?? [naiveMin, naiveMax]}
            allowDataOverflow={weightDomain != null}
            tick={{ fontSize: CHART_TICK_SIZE, fill: CHART_TICK }}
            tickLine={false}
            axisLine={false}
            width={44}
            tickFormatter={(v) => `${Math.round(v * 10) / 10}`}
            label={{
              value: weightUnit === "lb" ? t("nutritionPage.progress.hero.chart.lbAxis") : t("nutritionPage.progress.hero.chart.kgAxis"),
              // position "top" centers on the AXIS BOX (x + width/2) while the
              // tick numbers are end-anchored to its right edge, so the two
              // only line up when the numbers fill the box. dx re-centers the
              // unit over them across the 8px the box was widened by (36 → 44,
              // needed for 5-character values like "210.8").
              position: "top", offset: 16, dx: 4, fontSize: 11, fontWeight: 700, fill: "var(--primary)",
            }}
          />
          <Tooltip
            content={<GoalTooltip weightUnit={weightUnit} projColor={projColor} onMeasureWidth={onMeasureWidth} />}
            cursor={{ stroke: "var(--primary)", strokeWidth: 1 }}
            position={tooltipPosition}
          />
          <Legend
            content={<ChartLegend coloredText />}
            payload={legendPayload}
            wrapperStyle={{ paddingTop: 8 }}
          />

          {/* Goal line */}
          <ReferenceLine
            y={targetWeight}
            stroke={GOAL_COLOR}
            strokeDasharray="5 4"
            label={{ value: g(t, "chart.goalLine", { value: targetWeight, unit: weightUnit }), position: "insideBottomLeft", fontSize: 12, fill: GOAL_COLOR }}
          />

          {/* Projected trend to the target date */}
          <Line
            name={g(t, "chart.projected")}
            dataKey="projected"
            stroke={projColor}
            strokeWidth={2}
            strokeDasharray="5 4"
            dot={false}
            connectNulls
            type="linear"
            isAnimationActive={false}
          />

          {/* Actual weigh-ins */}
          <Line
            name={g(t, "chart.weight")}
            dataKey="actual"
            stroke={ACTUAL_COLOR}
            strokeWidth={2}
            dot={false}
            connectNulls
            type="monotone"
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
});

GoalChart.propTypes = {
  series: PropTypes.array.isRequired,
  targetWeight: PropTypes.number.isRequired,
  projColor: PropTypes.string.isRequired,
  weightUnit: PropTypes.string.isRequired,
};

/* ---- goal-set display ----------------------------------------------------- */

const GoalSetDisplay = memo(({ goalData, goalSeries, athlete, units }) => {
  const { t } = useTranslation("athlete-portal");
  const {
    targetWeight, utcTargetDate,
    ratePerWeek, movingToward, weeksToReach, weeksToTarget,
    needChange, reqRate, extraDeficit, projAtTarget, kgShort, paceState, isStale,
  } = goalData;
  const absRate = ratePerWeek == null ? null : Math.abs(ratePerWeek);
  const isImperial = units === "imperial";
  const weightUnit = isImperial ? "lb" : "kg";

  // goalData is always in kg (DB storage unit) — convert to the coach's
  // preferred unit only for what's rendered/typed here.
  const displayTargetWeight = toDisplayWeight(targetWeight, isImperial);
  const displayAbsRate = toDisplayRate(absRate, isImperial);
  const displayRatePerWeek = toDisplayRate(ratePerWeek, isImperial);
  const displayReqRate = toDisplayRate(reqRate, isImperial);
  const displayProjAtTarget = toDisplayWeight(projAtTarget, isImperial);
  const displayKgShort = toDisplayWeight(kgShort, isImperial);

  // "Adjust" opens the edit/clear modal — see GoalAdjustModal.js. The modal
  // submits directly (submit-form, same as GoalEmptyForm.js), so there's no
  // save/clear callback to wire here — a successful edit/delete triggers
  // Rill's own full navigation, same as creating a goal already does.
  const [isAdjustOpen, setIsAdjustOpen] = useState(false);

  // "good" = the projection is on pace or ahead (paceState, the same verdict
  // the Bodyweight trend card gives), for gain and loss goals alike.
  let projToken = "warn";
  if (projAtTarget == null) projToken = null;
  else if (paceState === "onPace" || paceState === "ahead") projToken = "good";
  const projValueClass = projToken ? `np-hero__card-value np-hero__card-value--${projToken}` : "np-hero__card-value";

  const displayGoalSeries = useMemo(() => {
    if (!isImperial) return goalSeries;
    return goalSeries.map((pt) => ({
      ...pt,
      actual: toDisplayWeight(pt.actual, true),
      projected: toDisplayWeight(pt.projected, true),
    }));
  }, [goalSeries, isImperial]);

  const narrative = buildGoalNarrative({
    ratePerWeek: displayRatePerWeek,
    isStale,
    movingToward, paceState, weeksToReach, weeksToTarget,
    targetWeight: displayTargetWeight,
    utcTargetDate,
    reqRate: displayReqRate,
    extraDeficit,
    weightUnit,
    t,
  });
  const goalState = ratePerWeek == null
    ? null
    : goalStateLabel({ movingToward, paceState, t });

  return (
    <div className="np-goal">
      <GoalHeader chip={goalState} />

      {/* Projection chart — full width above the cards */}
      {goalSeries.length > 0 && (
        <div>
          <GoalChart
            series={displayGoalSeries}
            targetWeight={displayTargetWeight}
            projColor={GOAL_STATE_COLORS[projToken]?.text ?? ACTUAL_COLOR}
            weightUnit={weightUnit}
          />
        </div>
      )}

      {/* Goal + Projected then cards + narrative, all in one row */}
      <div className="np-goal__cards-row">
        <div className="np-hero__card np-hero__card--split">
          <div>
            <span className="np-hero__card-label">{g(t, "goalLabel")}</span>
            <p className="np-hero__card-value">{displayTargetWeight} {weightUnit}</p>
            <p className="np-hero__card-sub">
              {g(t, "by")} {moment(String(utcTargetDate), "YYYYMMDD").format("MMM D")}
            </p>
          </div>

          <button
            type="button"
            data-button-type="secondary"
            className="np-hero__adjust-btn"
            onClick={() => setIsAdjustOpen(true)}
          >
            <SvgIcon src={EDIT_GRADIENT_ICON} classNames="np-plan-bar__cta-icon" />
            <span className="np-plan-bar__cta-label">{t("nutritionPage.progress.hero.adjust")}</span>
          </button>
        </div>
        <div className="np-hero__card">
          <div className="np-hero__card-label">{g(t, "projectedThen")}</div>
          {projAtTarget == null ? (
            <p className="np-hero__card-value" style={{ color: "var(--white-50)" }}>—</p>
          ) : (
            <>
              <p className={projValueClass}>
                {displayProjAtTarget} {weightUnit}
              </p>
              <p className="np-hero__card-sub">
                {kgShortLabel(displayKgShort, needChange, weightUnit, t)}
              </p>
            </>
          )}
        </div>

        {/* Narrative text + caption — shares the row with the Goal/Projected cards,
            caption sits in its own container directly below the narrative box. */}
        {narrative && (
          <div className="np-goal__narrative-col">
            <div className="np-goal__narrative">
              <SvgIcon src="/img/new-icons/info-circle.svg" useCurrentColor classNames="np-goal__narrative-icon" />
              <p>{narrative}</p>
            </div>
            {displayAbsRate != null && (
              <p className="np-goal__caption">
                {g(t, "captionTrend", { rate: displayAbsRate, unit: weightUnit })}
              </p>
            )}
          </div>
        )}
      </div>

      <GoalAdjustModal
        isOpen={isAdjustOpen}
        onClose={() => setIsAdjustOpen(false)}
        goalData={goalData}
        athlete={athlete}
        units={units}
      />
    </div>
  );
});

GoalSetDisplay.propTypes = {
  goalData: PropTypes.object.isRequired,
  goalSeries: PropTypes.array,
  athlete: PropTypes.object,
  units: PropTypes.string,
};

/* ---- empty state ------------------------------------------------------- */

// Empty-state: form that submits to the Rill action to create the goal.
// Uses submit-form with a nested body (matching AddGoal.js's proven bodyWeight
// goal payload) rather than a native form post — a plain <form> would need the
// backend to expand bracket-notation field names ("details[targetWeight]")
// into a nested object, which isn't a pattern used anywhere else in the app.
const GoalEmptyForm = memo(({ units = "metric", athlete }) => {
  const { t } = useTranslation("athlete-portal");
  const [targetWeight, setTargetWeight] = useState("");
  const [targetDate, setTargetDate] = useState(() => moment().add(12, "weeks").toDate());
  const athleteId = String(athlete?._id || "");
  const isImperial = units === "imperial";
  const weightUnit = isImperial ? "lbs" : "kg";
  const canSetGoal = Boolean(targetWeight && targetDate);

  // The coach types in their preferred unit, but details.targetWeight is
  // always stored in kg — convert before it hits the request body.
  const parsedTargetWeight = Number.parseFloat(targetWeight);
  const targetWeightKg = Number.isNaN(parsedTargetWeight) ? null : toKg(parsedTargetWeight, isImperial);

  // Submit the goal to the Rill action to create the goal.
  const handleSubmit = () => {
    if (!canSetGoal) return;
    submit(`/actions/client-goals/add-new-goal?proxyAthlete=${athleteId}`, {
      method: "POST",
      body: {
        type: "bodyWeight",
        startDate: moment().format("YYYY-MM-DD"),
        utcStartDate: Number(moment().format("YYYYMMDD")),
        targetDate: moment(targetDate).format("YYYY-MM-DD"),
        utcTargetDate: Number(moment(targetDate).format("YYYYMMDD")),
        details: { targetWeight: targetWeightKg },
      },
    });
  };

  return (
    <div className="np-goal np-goal--empty">
      <GoalHeader chip={{ label: g(t, "stateMissing"), token: "bad" }} />
      <p className="np-goal__field-label mb-10">
        {g(t, "emptyDescription")}
      </p>
      <div className="np-goal__empty-form">
        <span className="np-goal__field-label np-goal__field-label--empty">
          {g(t, "targetWeightLabel", { unit: weightUnit })}
        </span>
        <span className="np-goal__field-label np-goal__field-label--empty">
          {g(t, "byDate")}
        </span>
        <span />

        <input
          type="number"
          value={targetWeight}
          onChange={(e) => setTargetWeight(e.target.value)}
          placeholder="80"
          className="np-goal__input"
        />
        <div>
          <DateInput
            name="goal-target-date"
            value={targetDate}
            onChange={(date) => setTargetDate(date)}
            customClassNames="np-goal__input"
          />
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSetGoal}
          data-button-type="primary"
          className="np-goal__submit-btn"
        >
          {g(t, "setGoal")}
        </button>
      </div>
    </div>
  );
});

GoalEmptyForm.propTypes = {
  units: PropTypes.string,
  athlete: PropTypes.object,
};

/* ---- exported bodyweight goal section ----------------------------------- */

// goalData/goalSeries are computed upstream in SummaryView.js (same pattern as
// SummaryHero's series/expenditure) — this component is presentation-only.
export const BodyweightGoal = memo(({ goalData, goalSeries, athlete, units }) => {
  if (goalData) {
    return (
      <GoalSetDisplay
        goalData={goalData}
        goalSeries={goalSeries}
        athlete={athlete}
        units={units}
      />
    );
  }
  return <GoalEmptyForm units={units} athlete={athlete} />;
});

BodyweightGoal.propTypes = {
  goalData: PropTypes.object,
  goalSeries: PropTypes.array,
  athlete: PropTypes.object,
  units: PropTypes.string,
};
